import WithLabel from 'components/atoms/WithLabel'
import { postService } from 'core/services'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../core/i18n'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'
import { Button, InputRadio, InputText, InputTextArea, Select, Toggle } from '../atoms'
import { CheckBoxes } from '../molecules'
import { Form } from '../molecules'
import InputLabel from 'components/atoms/InputLabel'
import ReactSelect from 'react-select'

declare var window: interfaces.IWindow

const BASIC_INFO_FIELDS = {
  family_owner: 'family_owner',
  post_type: 'post_type',
  category_id: 'category_id',
  tag_ids: 'tag_ids',
  duration: 'duration',
  max_people: 'max_people',
  min_people: 'min_people',
  cutoff_times: 'cutoff_times',
  pickup: 'pickup',
  area_id: 'area_id',
  language_ids: 'language_ids',
  display_language_ids: 'display_language_ids',
  private_quantity: 'private_quantity',
  tour_group_max_attendees: 'tour_group_max_attendees',
  booking_max_people: 'booking_max_people',
  instant_booking: 'instant_booking',
  bokun_product_code: 'bokun_product_code',
  short_name: 'short_name',
  expandable: 'expandable',
  alert_remaining_number: 'alert_remaining_number',
  cancel_policy_id: 'cancel_policy_id',
}

const TOUR_TYPES = [
  {
    id: 1,
    name: 'Private',
    name_ja: 'プライベート',
    value: 'private',
  },
  {
    id: 2,
    name: 'Group',
    name_ja: 'グループ',
    value: 'group',
  },
]

const FAMILY_POST_OPTIONS = [
  {
    id: 1,
    name: 'Disable',
    name_ja: '無効',
    value: 'false',
  },
  {
    id: 2,
    name: 'Enable',
    name_ja: '有効',
    value: 'true',
  },
]

const INSTANT_BOOKING_OPTIONS = [
  {
    id: 1,
    name: 'Disable',
    name_ja: '無効',
    value: 'false',
  },
  {
    id: 2,
    name: 'Enable',
    name_ja: '有効',
    value: 'true',
  },
]

const PEOPLE_COUNTS = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  21,
  22,
  23,
  24,
  25,
]
interface IErrors {
  [key: string]: string | null
}

interface IProps {
  post: interfaces.ICustomPost
  isNew: boolean
  categories: any
  tags: any
  languages: any
  areas: interfaces.IMasterItem[]
  display_languages: any[]
  cancel_policies: any[]
  slug: string
  user: any
  is_agent: boolean
  setIsNew(isNew: boolean): void
}

interface IEditBokunLinkModalBodyProps {
  post: interfaces.ICustomPost
  formRef: any
  onEditHandler(id: number, values: any): void
}

interface IEditSlugModalBodyProps {
  post: interfaces.ICustomPost
  slug: string
  formRef: any
  onEditHandler(initialValues: any, values: any): void
}

const formatSelectOptions = options => {
  return options.map((option: { id: number; name: string; name_ja: string }) => ({
    value: option.id,
    label: I18n.locale === 'ja' && option.name_ja ? option.name_ja : option.name,
  }))
}

// slug編集モーダル
const EditSlugModalBody: React.FC<IEditSlugModalBodyProps> = props => {
  const [modalErrors, setModalErrors] = React.useState({ slug: null })

  return (
    <div>
      <Form
        ref={props.formRef}
        fields={{ slug: 'slug' }}
        handleUpdateForm={updatedErrors => setModalErrors(updatedErrors)}
        handleSubmit={(initialValues, values) => props.onEditHandler(props.post.id, values)}
      >
        <InputTextArea
          required={true}
          postEdit={true}
          name="slug"
          label={I18n.t('post.slug')}
          error={modalErrors.slug}
          defaultValue={props.post.slug}
        />
      </Form>
      <FormComment>{I18n.t('post.edit_slug_comment')}</FormComment>
    </div>
  )
}

// Bokun連携編集モーダル
const EditBokunLinkModalBody: React.FC<IEditBokunLinkModalBodyProps> = props => {
  const [modalErrors, setModalErrors] = React.useState({ bokun_activity_id: null })
  const linkedOption = props.post.bokun_activity
    ? { label: props.post.bokun_activity.title, value: props.post.bokun_activity.activity_id }
    : null
  const unlinkedOption = { label: '連携なし', value: '' }
  const [bokunActivities, setBokunActivities] = React.useState([])
  const [selectedActivity, setSelectedActivity] = React.useState<any>(linkedOption)
  const [isLoading, setIsLoading] = React.useState(true)
  const [fetchError, setFetchError] = React.useState<string | null>(null)

  // 開いたときにBokunのアクティビティを同期して選択肢にする
  React.useEffect(() => {
    const syncBokunActivities = async () => {
      try {
        const { data } = await postService.getBokunActivities()
        setBokunActivities(
          [unlinkedOption].concat(
            data.map(item => ({ label: item.title, value: item.activity_id }))
          )
        )
      } catch (e) {
        // 取得できなくても、連携を外す・今の連携先のままにする操作はできるようにする
        setBokunActivities(linkedOption ? [unlinkedOption, linkedOption] : [unlinkedOption])
        const errors = e.response?.data?.errors
        setFetchError(typeof errors === 'string' ? errors : I18n.t('generic.error'))
      } finally {
        setIsLoading(false)
      }
    }
    syncBokunActivities()
  }, [])

  return (
    <div>
      <Form
        ref={props.formRef}
        fields={{ bokun_activity_id: 'bokun_activity_id' }}
        handleUpdateForm={updatedErrors => setModalErrors(updatedErrors)}
        handleSubmit={(initialValues, values) => props.onEditHandler(props.post.id, values)}
      >
        <InputLabel label={I18n.t('post.bokun_activity_id')} />
        <ReactSelect
          options={bokunActivities}
          value={selectedActivity}
          onChange={option => setSelectedActivity(option && option.value ? option : null)}
          isLoading={isLoading}
          isDisabled={isLoading}
          placeholder={isLoading ? 'Loading...' : '連携なし'}
          // valueも検索条件に含まれるため
          filterOption={({ label }, input) =>
            (label || '').toLowerCase().includes(input.toLowerCase())
          }
          // モーダル(overflow: hidden, z-index: 6000)の外にメニューを出して、切れたり隠れたりしないようにする
          menuPortalTarget={document.querySelector('body')}
          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        />
        {/* ReactSelectはisDisabledの間hidden inputを描画せずFormの値取得が落ちるため、自前で持つ */}
        <input type="hidden" name="bokun_activity_id" value={selectedActivity?.value || ''} />
        {modalErrors.bokun_activity_id && (
          <ModalErrorMessage>{modalErrors.bokun_activity_id}</ModalErrorMessage>
        )}
      </Form>
      {isLoading && (
        <ModalLoadingMessage>{I18n.t('post.bokun_activities_loading')}</ModalLoadingMessage>
      )}
      {fetchError && <ModalErrorMessage>{fetchError}</ModalErrorMessage>}
    </div>
  )
}

const PostBasicInfo: React.FC<IProps> = props => {
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false)
  const [errors, setErrors] = React.useState<IErrors>({})
  const [showPrivateQuantity, setShowPrivateQuantity] = React.useState(
    props.post.post_type === 'private'
  )
  // Bokun連携はモーダルから別APIで保存し、保存後は画面を読み込み直すので props の値だけ見ればよい
  const linkedBokunActivity = props.post.bokun_activity
  const bookingMaxPeople =
    props.post.booking_max_people || props.post.bokun_activity?.max_per_booking || 100
  const [post, setPost] = React.useState(props.post)
  const [familyOwner, setFamilyOwner] = React.useState<boolean>(props.post.family_owner ?? false)
  const [expandable, setExpandable] = React.useState<boolean>(props.post.expandable)
  const formRef = React.useRef(null)
  const [pickup, setPickup] = React.useState(post?.pickup)
  const handleChangePickup = React.useCallback(e => {
    setPickup(e.target.checked)
  }, [])

  const handleUpdateForm = React.useCallback((updatedErrors, updatedIsSubmitEnabled, values) => {
    setErrors(updatedErrors)
    setIsSubmitEnabled(updatedIsSubmitEnabled)
  }, [])
  const admin = props.user.admin
  const agent = props.user.agent

  const handleFormSubmit = async (_, values) => {
    const params: any = { ...values }

    if (post) {
      params.id = post.id
    }

    if (!props.user.admin) {
      delete params.pickup
    }

    params.instant_booking = params.instant_booking === 'true'
    params.family_owner = params.family_owner === 'true'
    // privateの場合はmax_peopleと同じ値をbooking_max_peopleに設定
    if (params.post_type === 'private') {
      params.booking_max_people = params.max_people
    }

    if (props.isNew) {
      try {
        const { edit_post_path } = await postService.create({ ...params })
        location.href = edit_post_path
      } catch (e) {
        console.error(e)
      }
    } else {
      try {
        const { post: updatedPost, flush } = await postService.update({ ...params })
        setPost(updatedPost)
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
        if (flush.type === 'success') {
          location.reload()
        }
      } catch (e) {
        console.error(e)
      }
    }
  }
  // slug編集
  const onEditHandler = async (id, param) => {
    const { post: updatedPost, flush } = await postService.updateSlug({ id, ...param })
    setPost(updatedPost)
    window.globalModal.closeModal()
    location.href = `/agent/posts/${param.slug}/edit`
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
  }

  const showEditSlugModal = async () => {
    window.globalModal.showModal({
      title: I18n.t('post.edit_slug'),
      body: (
        <EditSlugModalBody
          post={post}
          slug={post.slug}
          formRef={formRef}
          onEditHandler={onEditHandler}
        />
      ),
      closeText: I18n.t('generic.cancel'),
      submitText: I18n.t('generic.update'),
      handleSubmit: () => formRef.current.handleFormSubmit(),
    })
  }

  // Bokun連携編集
  const onEditBokunLinkHandler = async (id, param) => {
    await postService.updateBokunLink({ id, ...param })
    window.globalModal.closeModal()
    // 連携先が変わると商品コード・上限人数・各欄の編集可否も変わるためリロードして最新の値を表示する。
    location.reload()
  }

  const showEditBokunLinkModal = async () => {
    window.globalModal.showModal({
      title: I18n.t('post.bokun_activity_id'),
      body: (
        <EditBokunLinkModalBody
          post={post}
          formRef={formRef}
          onEditHandler={onEditBokunLinkHandler}
        />
      ),
      closeText: I18n.t('generic.cancel'),
      submitText: I18n.t('generic.update'),
      handleSubmit: () => formRef.current.handleFormSubmit(),
    })
  }

  const togglePrivateQuantity = React.useCallback(event => {
    if (event.target.value === 'private') {
      setShowPrivateQuantity(true)
    } else {
      setShowPrivateQuantity(false)
    }
  }, [])
  const getConvertedOptions = arr =>
    arr.map(data => ({
      id: String(data.id),
      label: `${I18n.locale === 'ja' ? data.name_ja : data.name}`,
      value: data.value,
    }))

  const displayLanguagesChecked = React.useMemo(() => {
    if (post?.post_details && post?.post_details.length > 0) {
      return post.post_details.map(({ display_language_id }) => ({
        id: display_language_id,
      }))
    }
    return props.display_languages.map(({ id }) => ({ id }))
  }, [])

  return (
    <>
      <h2 className="Editor_Title">{I18n.t('post.input_basic_info')}</h2>
      <Form
        fields={BASIC_INFO_FIELDS}
        handleUpdateForm={handleUpdateForm}
        handleSubmit={handleFormSubmit}
      >
        <ul>
          <FormItem hide={!agent}>
            <InputText
              required={!!agent}
              name="short_name"
              label={I18n.t('post.short_name')}
              placeholder={I18n.t('post.short_name')}
              defaultValue={String(post.short_name || '')}
              error={errors.short_name}
            />
          </FormItem>
          {/* Bokun連携は保存済みのツアーに対して専用API(update_bokun_link)で行うため、新規作成の画面では出さない(作成後に編集画面で連携する) */}
          {props.is_agent && !props.isNew && (
            <FormItem>
              <InputText
                readonly={true}
                postEdit={true}
                name="bokun_activity"
                label={I18n.t('post.bokun_activity_id')}
                defaultValue={linkedBokunActivity?.title || '連携なし'}
              />
              <div className="buttons">
                <Button small={true} handleClick={showEditBokunLinkModal}>
                  <span>{I18n.t('generic.edit')}</span>
                </Button>
              </div>
            </FormItem>
          )}
          <FormItem hide={!props.is_agent}>
            <InputText
              required={false}
              name="bokun_product_code"
              label={I18n.t('post.bokun_product_code')}
              placeholder="21490P..."
              defaultValue={String(post.bokun_product_code || '')}
              error={errors.bokun_product_code}
            />
          </FormItem>
          <FormItem>
            {agent ? (
              <InputRadio
                options={getConvertedOptions(FAMILY_POST_OPTIONS)}
                defaultValue={String(post.family_owner)}
                name="family_owner"
                label={I18n.t('post.family_owner')}
                onChangeHandler={e => setFamilyOwner(e.target.value === 'true')}
              />
            ) : (
              <input type="hidden" name="family_owner" value="false" />
            )}
          </FormItem>
          <FormItem>
            <InputRadio
              options={getConvertedOptions(TOUR_TYPES)}
              defaultValue={post?.post_type}
              name="post_type"
              label={I18n.t('post.tour_type')}
              onChangeHandler={togglePrivateQuantity}
            />
          </FormItem>
          <FormItem hide={!agent || showPrivateQuantity === false}>
            <InputText
              required={false}
              name="private_quantity"
              label={I18n.t('post.private_quantity')}
              placeholder={I18n.t('post.private_quantity')}
              defaultValue={String(post.private_quantity || 100)}
              error={errors.private_quantity}
            />
          </FormItem>
          <FormItem>
            <InputRadio
              options={getConvertedOptions(INSTANT_BOOKING_OPTIONS)}
              defaultValue={String(post.instant_booking)}
              name="instant_booking"
              label={I18n.t('post.instant_booking')}
            />
          </FormItem>
          <FormItem>
            <Select
              required={true}
              name="area_id"
              label={I18n.t('generic.area')}
              options={formatSelectOptions(props.areas)}
              defaultValue={String(post.area_id)}
              error={errors.area_id}
            />
          </FormItem>
          {!props.isNew && (
            <FormItem>
              <InputText
                readonly={true}
                postEdit={true}
                name="slug"
                label={I18n.t('post.slug')}
                placeholder={I18n.t('post.slug')}
                defaultValue={post?.slug}
                error={errors.slug}
              />
              <div className="buttons">
                <Button small={true} handleClick={showEditSlugModal}>
                  <span>{I18n.t('post.edit_slug')}</span>
                </Button>
              </div>
            </FormItem>
          )}
          <FormItem>
            <Select
              name="duration"
              label={I18n.t('post.duration')}
              options={constants.POST_DURATIONS.map(hours => ({ label: hours, value: hours }))}
              defaultValue={post ? String(post.duration) : ''}
              error={errors.duration}
            />
          </FormItem>
          <FormItem>
            <Select
              name="max_people"
              label={I18n.t('post.max_people')}
              options={PEOPLE_COUNTS.map(count => ({ label: count, value: count }))}
              defaultValue={post ? String(post.max_people) : ''}
              disabled={!showPrivateQuantity && !!linkedBokunActivity}
              error={errors.max_people}
            />
            {!showPrivateQuantity && !!linkedBokunActivity && (
              <WithLabel label="">
                <div className="annotation">{I18n.t('post.bokun_annotation')}</div>
              </WithLabel>
            )}
          </FormItem>
          <FormItem>
            <Select
              name="min_people"
              label={I18n.t('post.min_people')}
              options={PEOPLE_COUNTS.map(count => ({ label: count, value: count }))}
              defaultValue={post ? String(post.min_people) : ''}
              disabled={!showPrivateQuantity && !!linkedBokunActivity}
              error={errors.min_people}
            />
            {!showPrivateQuantity && !!linkedBokunActivity && (
              <WithLabel label="">
                <div className="annotation">{I18n.t('post.bokun_annotation')}</div>
              </WithLabel>
            )}
          </FormItem>
          <FormItem hide={!agent || showPrivateQuantity}>
            <InputText
              required={false}
              name="booking_max_people"
              label={I18n.t('post.booking_max_people')}
              placeholder={I18n.t('post.booking_max_people')}
              defaultValue={String(bookingMaxPeople)}
              readonly={!!linkedBokunActivity}
              error={errors.booking_max_people}
            />
            {!showPrivateQuantity && !!linkedBokunActivity && (
              <WithLabel label="">
                <div className="annotation">{I18n.t('post.bokun_annotation')}</div>
              </WithLabel>
            )}
          </FormItem>
          <FormItem hide={!agent}>
            <InputText
              required={false}
              name="tour_group_max_attendees"
              label={I18n.t('post.tour_group_max_attendees')}
              placeholder={I18n.t('post.tour_group_max_attendees')}
              defaultValue={String(post.tour_group_max_attendees || 100)}
              error={errors.tour_group_max_attendees}
            />
          </FormItem>
          <FormItem hide={!agent}>
            <InputRadio
              options={getConvertedOptions(INSTANT_BOOKING_OPTIONS)}
              defaultValue={String(post.expandable)}
              name="expandable"
              label={I18n.t('post.expandable')}
              onChangeHandler={e => setExpandable(e.target.value === 'true')}
            />
          </FormItem>
          <FormItem hide={!agent || !expandable}>
            <Select
              name="alert_remaining_number"
              label={I18n.t('post.alert_remaining_number')}
              options={[...Array(26)].map((_, count) => ({ label: count, value: count }))}
              defaultValue={
                post?.alert_remaining_number ? String(post.alert_remaining_number) : '0'
              }
              error={errors.alert_remaining_number}
            />
            <WithLabel label="">
              <div className="annotation">{I18n.t('post.alert_number_annotation')}</div>
            </WithLabel>
          </FormItem>
          {familyOwner ? (
            <input type="hidden" name="cutoff_times" value="0" />
          ) : (
            <FormItem>
              <InputText
                name="cutoff_times"
                label={I18n.t('post.cutoff_times')}
                placeholder={I18n.t('post.cutoff_times')}
                defaultValue={post.cutoff_times}
                disabled={!!linkedBokunActivity}
                readonly={!!linkedBokunActivity}
                error={errors.cutoff_times}
              />
              {!!linkedBokunActivity && (
                <WithLabel label="">
                  <div className="annotation">{I18n.t('post.bokun_annotation')}</div>
                </WithLabel>
              )}
            </FormItem>
          )}
          <FormItem>
            <CheckBoxes
              options={props.languages}
              checked={post?.languages}
              title={I18n.t('post.language')}
              name="language_ids"
            />
          </FormItem>
          <FormItem>
            <CheckBoxes
              options={props.display_languages}
              checked={displayLanguagesChecked}
              title={I18n.t('post.display_language')}
              name="display_language_ids"
              required={true}
            />
            <WithLabel label="">
              <div className="annotation">{I18n.t('post.display_language_note')}</div>
            </WithLabel>
          </FormItem>
          <FormItem>
            <InputRadio
              options={getConvertedOptions(
                props.categories.map(category =>
                  Object.assign(category, { value: String(category.id) })
                )
              )}
              defaultValue={String(post?.category?.id)}
              name="category_id"
              label={I18n.t('post.category')}
            />
          </FormItem>
          <FormItem>
            <CheckBoxes
              options={props.tags}
              checked={post?.tags}
              title={I18n.t('generic.tag')}
              name="tag_ids"
            />
          </FormItem>
          <FormItem>
            {props.is_agent ? (
              <InputRadio
                options={getConvertedOptions(
                  props.cancel_policies?.map(cancelPolicy =>
                    Object.assign(cancelPolicy, { value: String(cancelPolicy.id) })
                  ) || []
                )}
                defaultValue={String(
                  post?.cancel_policy_id || props.cancel_policies?.find(cp => cp.default_policy)?.id
                )}
                name="cancel_policy_id"
                label={I18n.t('activerecord.models.cancel_policy')}
              />
            ) : (
              <input type="hidden" name="cancel_policy_id" value="" />
            )}
          </FormItem>
          <FormItem hide={!admin}>
            <Toggle
              name="pickup"
              label={I18n.t('generic.pickup')}
              checked={pickup}
              onChangeHandler={handleChangePickup}
            />
          </FormItem>
        </ul>
        <div className="Editor_Footer">
          <Button primary={true} disabled={!isSubmitEnabled}>
            {props.isNew ? I18n.t('generic.create') : I18n.t('generic.update')}
          </Button>
        </div>
      </Form>
    </>
  )
}

const FormItem = styled.div`
  ${({ hide }) => hide && 'display: none'}

  & + & {
    margin-top: 16px;
  }

  .FormItem_Title {
    font-size: 15px;
    font-weight: bold;
  }

  .buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 5px;
    margin-bottom: 10px;
  }
  .FormItem_Note {
    padding-top: 16px;
    white-space: pre-wrap;
  }
  .annotation {
    color: red;
    font-size: 10px;
    white-space: pre-wrap;
    margin-top: 4px;
  }
`
const ModalErrorMessage = styled.div`
  font-size: 13px;
  color: ${constants.COLORS.cautionText};
  padding-top: 8px;
`
const ModalLoadingMessage = styled.div`
  font-size: 13px;
  color: ${constants.COLORS.subText};
  padding-top: 8px;
`
const FormComment = styled.div`
  font-size: 13px;
  font-weight: bold;
  padding-top: 16px;
  padding-bottom: 16px;
  // text-align: right;
  color: ${constants.COLORS.caution};
`

export default PostBasicInfo
